const ICS_NEWLINE = '\r\n';

interface CalendarInviteOptions {
  title: string;
  description?: string;
  location: string;
  start: Date;
  durationMinutes: number;
  organizerName?: string;
  organizerEmail?: string | null;
  attendeeName?: string;
  attendeeEmail?: string | null;
  timezone?: string;
  uid?: string;
}

function formatDateTime(value: Date, timeZone?: string): string {
  if (typeof timeZone === 'string' && timeZone.length > 0) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(value);

    const lookup = new Map(parts.map((part) => [part.type, part.value]));
    const year = lookup.get('year');
    const month = lookup.get('month');
    const day = lookup.get('day');
    const hour = lookup.get('hour');
    const minute = lookup.get('minute');
    const second = lookup.get('second');

    if (
      year !== undefined &&
      month !== undefined &&
      day !== undefined &&
      hour !== undefined &&
      minute !== undefined &&
      second !== undefined
    ) {
      return `${year}${month}${day}T${hour}${minute}${second}`;
    }
  }

  const year = value.getFullYear().toString();
  const month = (value.getMonth() + 1).toString().padStart(2, '0');
  const day = value.getDate().toString().padStart(2, '0');
  const hours = value.getHours().toString().padStart(2, '0');
  const minutes = value.getMinutes().toString().padStart(2, '0');
  const seconds = value.getSeconds().toString().padStart(2, '0');
  return `${year}${month}${day}T${hours}${minutes}${seconds}`;
}

function escapeText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

function addMinutes(base: Date, minutes: number): Date {
  const next = new Date(base.getTime());
  next.setMinutes(next.getMinutes() + minutes);
  return next;
}

function buildUid(explicitUid?: string): string {
  if (typeof explicitUid === 'string' && explicitUid.length > 0) {
    return explicitUid;
  }
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `showing-${Date.now().toString()}-${Math.random().toString(16).slice(2)}`;
}

export function createCalendarInvite(options: CalendarInviteOptions): string {
  const {
    title,
    description,
    location,
    start,
    durationMinutes,
    organizerName,
    organizerEmail,
    attendeeName,
    attendeeEmail,
    timezone,
    uid,
  } = options;

  const hasTimezone = typeof timezone === 'string' && timezone.length > 0;
  const startLabel = formatDateTime(start, hasTimezone ? timezone : undefined);
  const endLabel = formatDateTime(
    addMinutes(start, durationMinutes),
    hasTimezone ? timezone : undefined,
  );
  const nowLabel = new Date()
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z');
  const organizerLabel = organizerName ?? 'Strashin Developments';
  const attendeeLabel = attendeeName ?? 'Guest';
  const organizerEmailLabel = organizerEmail ?? '';
  const attendeeEmailLabel = attendeeEmail ?? '';
  const dtStartLine = hasTimezone
    ? `DTSTART;TZID=${timezone}:${startLabel}`
    : `DTSTART:${startLabel}`;
  const dtEndLine = hasTimezone ? `DTEND;TZID=${timezone}:${endLabel}` : `DTEND:${endLabel}`;

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//StrashinDevelopments//Showings//EN',
    'METHOD:REQUEST',
    'BEGIN:VEVENT',
    `UID:${buildUid(uid)}`,
    `DTSTAMP:${nowLabel}`,
    dtStartLine,
    dtEndLine,
    `SUMMARY:${escapeText(title)}`,
    `LOCATION:${escapeText(location)}`,
    `DESCRIPTION:${escapeText(description ?? '')}`,
    `ORGANIZER;CN=${escapeText(organizerLabel)}${organizerEmailLabel.length > 0 ? `:MAILTO:${organizerEmailLabel}` : ''}`,
    attendeeEmailLabel.length > 0
      ? `ATTENDEE;CN=${escapeText(attendeeLabel)};ROLE=REQ-PARTICIPANT:MAILTO:${attendeeEmailLabel}`
      : '',
    'END:VEVENT',
    'END:VCALENDAR',
    '',
  ].filter(Boolean);

  return lines.join(ICS_NEWLINE);
}
